import "./site-card.css"

import React, { useState } from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { Link, graphql } from "gatsby"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleRight } from "@fortawesome/free-solid-svg-icons"
import { faCheck } from "@fortawesome/free-solid-svg-icons"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "gatsby-plugin-react-i18next"

import Star from "@/images/icons/icon-star.inline.svg"
import StarHalf from "@/images/icons/icon-star-half.inline.svg"

export const query = graphql`
fragment SiteInformation on WpSite {
  uri
  date(formatString: "MM DD, YYYY")
  modified(formatString: "MM DD, YYYY")
  slug
  seoTitle
  seoDescription
  seoSchema
  menuOrder
  title
  author {
    node {
      name
      description
      slug
      avatar {
        url
        size
      }
    }
  }
  siteCategories {
    nodes {
      name
      slug
    }        
  }
  siteFields {
    title
    id
    coingeckoCoinIds
    coingeckoCountries
    languages
    currencies
    paymentMethods
    comparisonFeatures
    advancedComparisonFeatures
    url
    rating
    features {
      text
    }
    beginnerScore
    advancedScore
    stakingInfo
    featuresCoinsPage {
      text
    }
    pros {
      text
    }
    cons {
      text
    }
    ratings {
      assets
      easeOfUse
      fees
      overall
      safety
      staking
      tradingFeatures
      assetsLabel
      easeOfUseLabel
      feesLabel
      overallLabel
      safetyLabel
      stakingLabel
      tradingFeaturesLabel
    }
    content {
      mainContent
      summary
    }
    contentCoinsPage
    details {
      activeUsers
      assets
      dailyVolume
      exchangeHq
      liquidityScore
      makerFees
      regulation
      takerFees
      yearFounded
    }

    cta {
      buttonLabel
      text
      textDetailed
      screenshot {
        altText
        localFile {
          childImageSharp {
            gatsbyImageData(
              layout: FULL_WIDTH
            )
          }
        }
      }
    }
  }
  featuredImage {
    node {
      altText
      localFile {
        childImageSharp {
          gatsbyImageData(
            layout: FULL_WIDTH
          )
        }
      }
    }
  }
}
fragment SiteInformationSmall on WpSite {
  uri
  date(formatString: "MM DD, YYYY")
  modified(formatString: "MM DD, YYYY")
  slug
  title
  siteFields {
    title
    id
    coingeckoCoinIds
    coingeckoCountries
    languages
    currencies
    paymentMethods
    comparisonFeatures
    advancedComparisonFeatures
    url
    rating
    features {
      text
    }
    beginnerScore
    advancedScore
    stakingInfo
    featuresCoinsPage {
      text
    }
    pros {
      text
    }
    cons {
      text
    }
    ratings {
      assets
      easeOfUse
      fees
      overall
      safety
      staking
      tradingFeatures
      assetsLabel
      easeOfUseLabel
      feesLabel
      overallLabel
      safetyLabel
      stakingLabel
      tradingFeaturesLabel
    }
  }
  featuredImage {
    node {
      altText
      localFile {
        childImageSharp {
          gatsbyImageData(
            layout: FULL_WIDTH
          )
        }
      }
    }
  }
}
`

export interface SiteScore {
  name?: string
  text?: string
  score: number
}

export interface Site {
  slug: string
  date: string
  modified: string
  title: string // Short name of the site
  seoTitle: string
  seoDescription: string
  seoSchema: string // encoded json+ld tag
  menuOrder: number
  author: {
    node: {
      name: string
      description: string
      slug: string
      avatar: {
        url: string
        size: string
      }
    }
  }
  siteCategories: {
    nodes: {
      name: string
      slug: string
    }[]
  }
  siteFields: {
    title: string // Contents of the H1 tag for the site
    id: string
    coingeckoCountries: string[]
    coingeckoCoinIds: string[]
    languages: string[]
    currencies: string[]
    paymentMethods: string[]
    comparisonFeatures: string[]
    advancedComparisonFeatures: string[]
    stakingInfo: string
    url: string
    rating: string
    content: {
      summary: string
      mainContent: string
    }
    contentCoinsPage: string
    details: {
      activeUsers: string
      assets: string
      dailyVolume: string
      exchangeHq: string
      liquidityScore: string
      makerFees: string
      regulation: string
      takerFees: string
      yearFounded: string
    }
    cta: {
      buttonLabel: string
      text: string
      textDetailed: string
      screenshot: any
    }
    features: {
      text: string
    }[]
    beginnerScore: number
    advancedScore: number
    featuresCoinsPage: {
      text: string
    }[]
    cons: {
      text: string
    }[]
    pros: {
      text: string
    }[]
    ratings: {
      assets: number
      easeOfUse: number
      fees: number
      overall: number
      safety: number
      staking: number
      tradingFeatures: number
      assetsLabel?: string
      easeOfUseLabel?: string
      feesLabel?: string
      overallLabel?: string
      safetyLabel?: string
      stakingLabel?: string
      tradingFeaturesLabel?: string
    }
  }
  featuredImage?: any
}

export function Rating (props: { className?: string, rating: string }) {
  const { t } = useTranslation()
  const rating = props.rating;
  let score = parseFloat(rating);
  let label = ""
  if (score > 8.5) {
    label = t("Excellent")
  } else if (score > 8.0) {
    label = t("Very good")
  } else if (score > 7.5) {
    label = t("Good")
  } else {
    label = t("Fair")
  }
  const classNames = [ "site-rating" ]
  if (props.className) {
    classNames.push(props.className)
  }

  return <div className={classNames.join(" ")}>
    <div className="rating-label">
      {label}
    </div>
    <div className="rating-score">
      {rating}
    </div>
  </div>
}

export function RatingDetailed (props: { className?: string, score: number, label: string }) {
  const { t } = useTranslation()
  const score = props.score;
  const classNames = [ "site-rating-detailed" ]
  if (props.className) {
    classNames.push(props.className)
  }

  const stars = []
  for (let i = 1; i <= 5; i++) {
    if (score >= i * 2 - 1) {
      stars.push(<Star key={i} />)
    } else if(score >= i * 2 - 1.5) {
      stars.push(<StarHalf key={i} />)
    }
  }

  return <div className={classNames.join(" ")}>
    <div className="rating-label">
      {score.toFixed(1)}
    </div>
    <div className="rating-score">
      <div className="stars">
        {stars}
      </div>
      <div className="label">
        {props.label}
      </div>
    </div>
  </div>
}

export function SiteCard (props: { site: Site }) {
  const { site } = props
  const image = {
    data: site.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData,
    alt: site.featuredImage?.node?.alt || ``,
  }

  return <div className="site-card">
    <Link className="site-card-info" to={`/exchanges/${site.slug}/`} itemProp="url">
      <div className="site-card-top">
        {image?.data && <GatsbyImage className="image" image={image.data} alt={image.alt} />}
      </div>
      <div className="excerpt" dangerouslySetInnerHTML={{__html: site.siteFields?.title}}></div>
    </Link>
  </div>
}

export function SiteLogo (props: { site: Site }) {
  const { site } = props
  const image = {
    data: site.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData,
    alt: site.featuredImage?.node?.alt || ``,
  }

  return <Link className="site-logo" to={`/exchanges/${site.slug}/`} itemProp="url">
    {image?.data && <GatsbyImage className="image" image={image.data} alt={image.alt} />}
  </Link>
}

export function SiteCardSmall (props: { site: Site }) {
  const { site } = props
  const image = {
    data: site.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData,
    alt: site.featuredImage?.node?.alt || ``,
  }

  return <a className="site-card-small" href={site.siteFields.url} target="_blank" rel="noopener nofollow noreferrer" itemProp="url">
    {image?.data && <GatsbyImage className="image" objectFit="contain" image={image.data} alt={image.alt} />}
    <span className="site-title">
      {site.title}
    </span>
    <FontAwesomeIcon icon={faAngleRight} />
  </a>
}

export function ProsCons (props: {
  pros?: { text: string }[]
  cons?: { text: string }[]
}) {
  const { t } = useTranslation()
  return <div className="site-pros-cons">
    <div className="site-features site-features-pros">
      <h3>{t("Pros")}</h3>
      {props.pros?.map((pro, i) => <div key={i} className="site-feature">
        <FontAwesomeIcon icon={faCheck} />
        {pro.text}
      </div>)}
    </div>

    <div className="site-features site-features-cons">
      <h3>{t("Cons")}</h3>
      {props.cons?.map((con, i) => <div key={i} className="site-feature">
        <FontAwesomeIcon icon={faTimes} />
        {con.text}
      </div>)}
    </div>
  </div>
}

export function SiteCardDetailed (props: { site: Site }) {
  const { site } = props
  const image = {
    data: site.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData,
    alt: site.featuredImage?.node?.alt || ``,
  }

  const screenshot = {
    data: site.siteFields?.cta?.screenshot?.localFile?.childImageSharp?.gatsbyImageData,
    alt: site.siteFields?.cta?.screenshot?.alt || ``,
  }

  return <div className="site-card-detailed">
    <div className="site-card site-card-counter">
      <div className="site-card-info">
        <h3 className="site-name">{site.siteFields.cta.text}</h3>
        <div className="site-link invisible">{site.title}</div>
        <Rating className="hidden-mobile" rating={site.siteFields.rating} />
      </div>
      <div>
        {screenshot?.data && <GatsbyImage className="image" image={screenshot.data} alt={screenshot.alt} />}
      </div>
      <div className="site-card-details">
        <div className="site-card-top">
          {image?.data && <GatsbyImage className="image" height={50} image={image.data} alt={image.alt} />}
        </div>
        <a className="button" target="_blank" rel="noopener nofollow noreferrer" href={site.siteFields.url} itemProp="url">
          {site.siteFields.cta.buttonLabel}
        </a>
        <Rating className="hidden-desktop" rating={site.siteFields.rating} />
      </div>
    </div>

    <div className="site-card-details">
      <div className="features">
        {site.siteFields.features?.map((feature, i) => {
          return <div key={i} className="feature">
            <FontAwesomeIcon icon={faCheck} />
            <strong>{feature.text}</strong>
          </div>
        })}
      </div>
    </div>

    <div className="site-content" dangerouslySetInnerHTML={{__html: site.siteFields.cta.textDetailed}}></div>

    <div className="site-card">
      <a className="site-card-info" href={site.siteFields.url} itemProp="url">
        <div className="site-card-top">
          {image?.data && <GatsbyImage className="image" image={image.data} alt={image.alt} />}
        </div>
        <div className="button">
          {site.siteFields.cta.buttonLabel}
        </div>
      </a>
    </div>
  </div>
}

export function SiteDetails (props: { site: Site }) {
  const { t } = useTranslation()
  const { site } = props
  const details = site.siteFields.details
  const fields = [
    [t("Exchange HQ"), 'exchangeHq'],
    [t("Year Founded"), 'yearFounded'],
    [t("Active Users"), 'activeUsers' ],
    [t("Regulation"), 'regulation' ],
    [t("Assets"), 'assets' ],
    [t("Maker Fees"), 'makerFees' ],
    [t("Taker Fees"), 'takerFees' ],
    [t("Daily Volume"), 'dailyVolume' ],
    [t("Liquidity Score"), 'liquidityScore' ],
  ].filter(([_, key]) => details && (key in details) && (details as any)[key]?.length > 0)
  
  if (fields.length === 0) { return <></> }
  const [open, setOpen] = useState(false)

  return <div className={`site-details ${open ? 'table-open' : 'table-closed' }`}>
    <h4 className="site-details-title" onClick={() => setOpen(!open)}>
      {t("exchange-info", { title: site.title })}
    </h4>
    <table className="table table-minimal-borders">
      <tbody>
      {fields.map(([title, key]) =>
        key in details && <tr key={key}>
          <td>{title}</td>
          <td>{(details as any)[key]}</td>
        </tr>
      )}
      </tbody>
    </table>
  </div>
}
